import React, { useEffect, useState } from "react";

import HeadquartersPage from "../../components/headquarters/HeadquartersPage";
import HeadquartersSection from "../../components/headquarters/HeadquartersSection";
import StatusPill from "../../components/headquarters/StatusPill";

export default function PlatformBillingControl() {
  const [tenants, setTenants] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  async function loadTenants() {
    try {
      const res = await fetch("/api/platform/billing/tenants", {
        credentials: "include",
      });

      const data = await res.json();
      setTenants(data);
    } catch (err) {
      setMsg("Could not load platform billing tenants.");
    }
  }

  useEffect(() => {
    loadTenants();
  }, []);

  async function runAction(tenantId, action) {
    setBusy(true);
    setMsg("");

    try {
      const res = await fetch(`/api/platform/billing/tenant/${tenantId}/${action}`, {
        method: "POST",
        credentials: "include",
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || "Action failed.");
      }

      setMsg(data.message || "Billing control updated.");
      await loadTenants();
    } catch (err) {
      setMsg(err.message || "Could not update billing control.");
    } finally {
      setBusy(false);
    }
  }

  const pageStyle = {
    padding: 30,
    color: "#ffffff",
  };

  const cardStyle = {
    background: "#13293d",
    borderRadius: 12,
    padding: 20,
    border: "1px solid rgba(255,255,255,0.08)",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: 16,
  };

  const thStyle = {
    textAlign: "left",
    padding: "10px 8px",
    color: "#9fb0c3",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  };

  const tdStyle = {
    padding: "12px 8px",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    verticalAlign: "top",
  };

  const buttonStyle = {
    marginRight: 8,
    marginBottom: 6,
    padding: "8px 10px",
    background: "#e0b84f",
    border: "none",
    borderRadius: 8,
    cursor: busy ? "not-allowed" : "pointer",
    color: "#0b1b2b",
    fontWeight: 700,
  };

  return (
    <HeadquartersPage
      title="Billing Control"
      subtitle="Platform-owner controls for tenant access, trials, paid mode, and suspension."
    >

      {msg && (
        <div
          style={{
            margin: "16px 0",
            padding: 12,
            borderRadius: 10,
            background: "rgba(0,194,255,0.08)",
            border: "1px solid rgba(0,194,255,0.25)",
            color: "#dbeafe",
          }}
        >
          {msg}
        </div>
      )}

      <HeadquartersSection
        title="Tenant Billing Authority"
        subtitle="Platform-level control over tenant billing access and workspace state."
      >

        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Tenant</th>
              <th style={thStyle}>Workspace</th>
              <th style={thStyle}>Tenant Type</th>
              <th style={thStyle}>Plan</th>
              <th style={thStyle}>Stripe Status</th>
              <th style={thStyle}>Billing Mode</th>
              <th style={thStyle}>Trial Ends</th>
              <th style={thStyle}>Suspended</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {tenants.map((tenant) => (
              <tr key={tenant.id}>
                <td style={tdStyle}>{tenant.name}</td>
                <td style={tdStyle}>{tenant.subdomain}</td>
                <td style={tdStyle}>
                  <StatusPill
                    label={title(tenant.tenant_type || "standard")}
                    tone={tenant.tenant_type === "beta" ? "blue" : "default"}
                  />
                </td>
                <td style={tdStyle}>{tenant.subscription_plan || "—"}</td>
                <td style={tdStyle}>{tenant.subscription_status || "—"}</td>
                <td style={tdStyle}>{tenant.billing_mode || "—"}</td>
                <td style={tdStyle}>{tenant.trial_ends_at || "—"}</td>
                <td style={tdStyle}>{tenant.is_suspended ? "Yes" : "No"}</td>
                <td style={tdStyle}>
                  <button
                    style={buttonStyle}
                    disabled={busy}
                    onClick={() => runAction(tenant.id, "set-free")}
                  >
                    Set Free
                  </button>

                  <button
                    style={buttonStyle}
                    disabled={busy}
                    onClick={() => runAction(tenant.id, "set-trial")}
                  >
                    Start Trial
                  </button>

                  <button
                    style={buttonStyle}
                    disabled={busy}
                    onClick={() => runAction(tenant.id, "set-paid")}
                  >
                    Set Paid
                  </button>

                  {tenant.is_suspended ? (
                    <button
                      style={buttonStyle}
                      disabled={busy}
                      onClick={() => runAction(tenant.id, "unsuspend")}
                    >
                      Unsuspend
                    </button>
                  ) : (
                    <button
                      style={buttonStyle}
                      disabled={busy}
                      onClick={() => runAction(tenant.id, "suspend")}
                    >
                      Suspend
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </HeadquartersSection>
    </HeadquartersPage>
  );
}

function title(value) {
  return String(value || "—")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
